import { Injectable } from '@angular/core';
import { Style } from 'src/app/shared/map-data-services/styles/style';
import { StyleService } from 'src/app/shared/map-data-services/styles/style.service';
import { LayerStyleGenerator } from '../../map-data-services/layer/layer-style-generator';
import { LayerSymbologyType, StyleSymbol } from '../../map-data-services/styles/style-symbology';
import { StyleSymbologyStreamService } from '../../map-data-services/styles/style-symbology-stream.service';
import { GeometryTypes } from '../utility/geometry-utils';

@Injectable({
    providedIn: 'root'
})
export class StylesStore {
    constructor(private styleService: StyleService, private styleSymbologyStream: StyleSymbologyStreamService) {}

    // Note: Styles are not automatically loaded on project or workspace change.
    // Rather they are loaded via the stylesStore
    // We also can't load just the style for the current workspace, so load them for the whole project instead

    // -------------------------------
    // Project Styles

    public async getStyle(projectId: string, styleId: string): Promise<Style> {
        return await this.styleService.getStyleById(projectId, styleId);
    }

    public async createStyle(projectId: string, styleProperties: any, isSymbolEnabled = false): Promise<Style> {
        let symbol: StyleSymbol;
        if (isSymbolEnabled && styleProperties.symbol && styleProperties.symbol.symbolDefinitionUuid) {
            symbol = await this.createStyleSymbol(styleProperties);
            styleProperties.symbolId = symbol.id;
        }
        let style = await this.styleService.createStyle(projectId, styleProperties);

        return style;
    }

    public async updateStyle(
        projectId: string,
        styleId: string,
        styleProperties: Style,
        geometryType: GeometryTypes,
        isSymbolEnabled = false
    ): Promise<Style> {
        let symbol: StyleSymbol;
        const styleBeforeSave = await this.getStyle(projectId, styleId);

        // Handle style symbols
        if (styleBeforeSave?.symbol.id) {
            if (!isSymbolEnabled) {
                await this.styleSymbologyStream.deleteStyleSymbol(styleBeforeSave.symbol);
            } else if (styleProperties.symbol.symbolDefinitionUuid && styleProperties.symbol.id) {
                symbol = await this.updateStyleSymbol(styleProperties);
                styleProperties.symbolId = symbol.id;
                styleProperties.symbol = symbol;
            }
        } else {
            if (isSymbolEnabled && styleProperties.symbol.symbolDefinitionUuid) {
                symbol = await this.createStyleSymbol(styleProperties);
                styleProperties.symbolId = symbol.id;
                styleProperties.symbol = symbol;
            }
        }

        // Set style name from symbolColor.
        if (geometryType === GeometryTypes.POINT) {
            if (styleProperties.symbol.type === LayerSymbologyType.CUSTOMIZABLE) {
                styleProperties.name = LayerStyleGenerator.decimalToHexRgb(styleProperties.symbol.color);
            } else {
                styleProperties.name = LayerStyleGenerator.decimalToHexRgb(styleProperties.symbol.fillColor);
            }
        } else if (geometryType === GeometryTypes.LINE) {
            styleProperties.name = LayerStyleGenerator.decimalToHexRgb(styleProperties.lineColor);
        } else if (geometryType === GeometryTypes.AREA) {
            styleProperties.name = LayerStyleGenerator.decimalToHexRgb(styleProperties.fillColor);
        }

        if (!styleProperties.image) {
            styleProperties.image = '';
        }

        let style = await this.styleService.updateStyle(projectId, styleId, styleProperties);
        style.styleOverrides = styleProperties.styleOverrides;

        return style;
    }

    public getOrCreateStyleByColor(projectId: string, color: string): Promise<Style> {
        return new Promise<Style>((resolve, reject) => {
            let newStyle = new Style();
            if (color) {
                newStyle.setColor(color);
            }
            newStyle.setName('style' + color);

            this.createStyle(projectId, newStyle).then(style2 => resolve(style2));
        });
    }

    public async createStyleSymbol(templateStyle: Style): Promise<StyleSymbol> {
        const symbol = templateStyle.symbol;

        return await this.styleSymbologyStream.createStyleSymbol(symbol);
    }

    public async updateStyleSymbol(templateStyle: Style): Promise<StyleSymbol> {
        const symbol = templateStyle.symbol;

        return await this.styleSymbologyStream.updateStyleSymbol(symbol);
    }
}
